import React, { useMemo } from 'react';
import {
  CalendarActionsController,
  CalendarActionsProvider,
  useCalendarActionsData,
} from '../../../../../services/calendarActionsProvider';
import {
  actionNames as calendarActionNames,
  deletableCalendarEntityKeys,
} from '../../../../../shared/configs/calendar';
import ToggleBlockIntervals from '../../../controllers/ToggleBlockIntervals';
import OpenAddCalendarIntervalsModal from '../../../controllers/OpenAddCalendarIntervalsModal';
import DeleteCalendarIntervals from '../../../controllers/DeleteCalendarIntervals';
import { useSearchParamsSelector } from '../../../../../shared/packages/searchParamsManager';
import {
  detailsPageRegisterModes,
  searchParamKeys,
  searchParamSelectors,
} from '../../../../../shared/configs/searchParams';
import OpenSetupIntervalForRegistration from '../../../controllers/OpenSetupIntervalForRegistration';
import SetReRegistrationMode from '../../../controllers/SetReRegistrationMode';
import DropdownActions from '../../../ui/DropdownActions';
import UploadAcceptanceRecords from '../../../controllers/UploadAcceptanceRecords';
import useDeleteRegistrationHandler from '../../../../../controllers/base/deleteRegistrationHandler';
import UpdateRegistrationFromCalendar from '../../../controllers/UpdateRegistrationFromCalendar';

const pageModeSelector = searchParamSelectors[searchParamKeys.detailsPageMode];

export const DayActions = ({ calendarManager, dayData, role }) => {
  const actionsData = calendarManager.getDayActionsData(...dayData);

  const pageMode = useSearchParamsSelector(pageModeSelector);

  if (!actionsData || detailsPageRegisterModes.has(pageMode)) {
    return null;
  }

  const { getActionNames, ...restActionsData } = actionsData;

  const actionNames = getActionNames({ role });

  return <BaseActions {...restActionsData} actionNames={actionNames} />;
};

export const DeskActions = ({ calendarManager, dayData, deskId }) => {
  const actionsData = calendarManager.getDeskActionsData(deskId, ...dayData);
  const pageMode = useSearchParamsSelector(pageModeSelector);

  return actionsData && !detailsPageRegisterModes.has(pageMode) ? (
    <BaseActions {...actionsData} />
  ) : null;
};

export const IntervalActions = ({
  calendarManager,
  dayData,
  interval,
  role,
}) => {
  const pageMode = useSearchParamsSelector(
    searchParamSelectors[searchParamKeys.detailsPageMode]
  );

  const [date] = dayData;

  const actionsData = useMemo(() => {
    return calendarManager.getIntervalActionsData(interval, date);
  }, [calendarManager, interval, date]);

  const { getActionNames, ...restActionsData } = actionsData;

  const actionNames = getActionNames({ pageMode, role });

  return actionNames ? (
    <BaseActions {...restActionsData} actionNames={actionNames} />
  ) : null;
};

function BaseActions({ actionNames, ...props }) {
  return (
    <CalendarActionsProvider {...props}>
      <CalendarActionsController
        component={CalendarActions}
        actions={getActions(actionNames)}
      />
    </CalendarActionsProvider>
  );
}

function CalendarActions(props) {
  return <DropdownActions type={DropdownActions.types.calendar} {...props} />;
}

function DeleteRegistration() {
  const { interval } = useCalendarActionsData();

  const deleteRegistration = useDeleteRegistrationHandler();

  const handleClick = () => {
    deleteRegistration(interval.id);
  };

  return <div onClick={handleClick}>Удалить запись</div>;
}

const actionComponents = {
  // day
  [calendarActionNames.blockDay]: (
    <ToggleBlockIntervals
      blocked
      errorMessage="Не удалось заблокировать интервалы"
    >
      Заблокировать интервалы
    </ToggleBlockIntervals>
  ),
  [calendarActionNames.unblockDay]: (
    <ToggleBlockIntervals
      blocked={false}
      errorMessage="Не удалось разблокировать интервалы"
    >
      Разблокировать интервалы
    </ToggleBlockIntervals>
  ),
  [calendarActionNames.addDayIntervals]: <OpenAddCalendarIntervalsModal />,
  [calendarActionNames.deleteDayIntervals]: (
    <DeleteCalendarIntervals
      deletableEntity={deletableCalendarEntityKeys.day}
    />
  ),
  [calendarActionNames.uploadDayRecords]: <UploadAcceptanceRecords />,

  // desk
  [calendarActionNames.blockDesk]: (
    <ToggleBlockIntervals
      blocked
      errorMessage="Не удалось заблокировать интервалы"
    >
      Заблокировать интервалы
    </ToggleBlockIntervals>
  ),
  [calendarActionNames.unblockDesk]: (
    <ToggleBlockIntervals
      blocked={false}
      errorMessage="Не удалось разблокировать интервалы"
    >
      Разблокировать интервалы
    </ToggleBlockIntervals>
  ),
  [calendarActionNames.addDeskIntervals]: <OpenAddCalendarIntervalsModal />,
  [calendarActionNames.deleteDeskIntervals]: (
    <DeleteCalendarIntervals
      deletableEntity={deletableCalendarEntityKeys.desk}
    />
  ),

  // interval
  [calendarActionNames.blockInterval]: (
    <ToggleBlockIntervals
      blocked
      errorMessage="Не удалось заблокировать интервал"
    >
      Заблокировать интервал
    </ToggleBlockIntervals>
  ),
  [calendarActionNames.unblockInterval]: (
    <ToggleBlockIntervals
      blocked={false}
      errorMessage="Не удалось разблокировать интервал"
    >
      Разблокировать интервал
    </ToggleBlockIntervals>
  ),
  [calendarActionNames.deleteInterval]: (
    <DeleteCalendarIntervals
      deletableEntity={deletableCalendarEntityKeys.interval}
    />
  ),

  [calendarActionNames.registration]: (
    <OpenSetupIntervalForRegistration>
      Записать
    </OpenSetupIntervalForRegistration>
  ),
  [calendarActionNames.reRegistration]: <SetReRegistrationMode />,
  [calendarActionNames.selectIntervalForReRegistration]: (
    <OpenSetupIntervalForRegistration>
      Выбрать этот интервал для перезаписи
    </OpenSetupIntervalForRegistration>
  ),
  [calendarActionNames.clearInterval]: <DeleteRegistration />,
  [calendarActionNames.updateRegistration]: <UpdateRegistrationFromCalendar />,
};

function getActions(actionNames) {
  return actionNames?.map((name) => ({
    name: name,
    component: actionComponents[name],
  }));
}
